import api from './api';

/**
 * Attendance Service
 * Handles attendance-related API calls
 * Backend populates student & course on GET/PUT
 */

const attendanceService = {
  /**
   * Get all attendance records (student & course populated)
   * @returns {Promise<Array>} - List of attendance records
   */
  getAllAttendance: async () => {
    try {
      const response = await api.get('/attendance');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get attendance record by ID
   * @param {string} id - Attendance record ID
   * @returns {Promise<Object>} - Attendance record
   */
  getAttendanceById: async (id) => {
    try {
      const response = await api.get(`/attendance/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Create attendance record
   * @param {Object} data - { student, course, ... } (fields depend on your Attendance model)
   * @returns {Promise<Object>} - Created record
   */
  createAttendance: async (data) => {
    try {
      const response = await api.post('/attendance', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update attendance record (returns with student & course populated)
   * @param {string} id - Attendance record ID
   * @param {Object} data - Data to update
   * @returns {Promise<Object>} - Updated record
   */
  updateAttendance: async (id, data) => {
    try {
      const response = await api.put(`/attendance/${id}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete attendance record
   * @param {string} id - Attendance record ID
   * @returns {Promise<Object>} - Deletion confirmation
   */
  deleteAttendance: async (id) => {
    try {
      const response = await api.delete(`/attendance/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get instructor-scoped attendance for the running secondary cohort.
   * @param {string} instructorId
   */
  getInstructorAttendance: async (instructorId) => {
    try {
      const response = await api.get(`/attendance/instructor/${instructorId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Mark attendance in bulk for an instructor's scoped students.
   * @param {Object} payload - { instructorId, date, records: [{ student, status, course? }] }
   */
  markInstructorAttendance: async (payload) => {
    try {
      const response = await api.post('/attendance/instructor/mark', payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Mark attendance in bulk for a secondary cohort grouped by field of study.
   * @param {Object} payload - { batchId, trade?, date, records: [{ student, status }] }
   */
  markBatchAttendance: async (payload) => {
    try {
      const response = await api.post('/attendance/batch/mark', payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

export default attendanceService;
